import React, { useState } from 'react';
import styles from './ApplicationForm.module.css';
import { ApplicationFormData, FormErrors, SubmissionState } from '../types';

interface ApplicationFormProps {
  onSuccess: () => void;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({ onSuccess }) => {
  const [formData, setFormData] = useState<ApplicationFormData>({
    fullName: '',
    email: '',
    company: '',
    role: '',
    linkedinUrl: '',
    useCase: '',
    honeypot: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submissionState, setSubmissionState] = useState<SubmissionState>(SubmissionState.IDLE);
  const [formStartTime] = useState<number>(Date.now());

  const validateField = (name: keyof ApplicationFormData, value: string): string | undefined => {
    switch (name) {
      case 'fullName':
        if (!value.trim()) return 'Dein Name ist erforderlich';
        if (value.trim().length < 2) return 'Dein Name muss mindestens 2 Zeichen lang sein';
        break;

      case 'email':
        if (!value.trim()) return 'Deine E-Mail-Adresse ist erforderlich';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Bitte gib eine gültige E-Mail-Adresse ein';
        break;

      case 'company':
        if (!value.trim()) return 'Deine Firma/Organisation ist erforderlich';
        break;

      case 'role':
        if (!value.trim()) return 'Deine Rolle/Titel ist erforderlich';
        break;

      case 'linkedinUrl':
        if (value.trim()) {
          const urlRegex = /^https?:\/\/(www\.)?linkedin\.com\/.+$/i;
          if (!urlRegex.test(value)) return 'Bitte gib eine gültige LinkedIn URL ein';
        }
        break;

      case 'useCase':
        if (!value.trim()) return 'Dein Interesse an der Toolbox ist erforderlich';
        if (value.trim().length < 20) return 'Bitte gib mindestens 20 Zeichen ein';
        if (value.length > 1000) return 'Maximum 1000 Zeichen erlaubt';
        break;
    }
    return undefined;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const error = validateField(name as keyof ApplicationFormData, value);
    if (error) {
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate all required fields
    Object.keys(formData).forEach((key) => {
      if (key !== 'honeypot' && key !== 'linkedinUrl') {
        const error = validateField(
          key as keyof ApplicationFormData,
          formData[key as keyof ApplicationFormData]
        );
        if (error) {
          newErrors[key as keyof FormErrors] = error;
        }
      }
    });

    // Validate optional LinkedIn URL if provided
    if (formData.linkedinUrl) {
      const error = validateField('linkedinUrl', formData.linkedinUrl);
      if (error) {
        newErrors.linkedinUrl = error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Bot detection: honeypot field should be empty
    if (formData.honeypot) {
      console.warn('Bot erkannt');
      return;
    }

    // Bot detection: form should take at least 3 seconds to fill
    const timeTaken = Date.now() - formStartTime;
    if (timeTaken < 3000) {
      console.warn('Formular wurde zu schnell ausgefüllt');
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    setSubmissionState(SubmissionState.SUBMITTING);

    try {
      // Get Web3Forms access key from environment variable
      const accessKey = process.env.REACT_APP_WEB3FORMS_KEY;

      if (!accessKey) {
        throw new Error('Web3Forms access key not configured');
      }

      // Submit to Web3Forms API
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: accessKey,
          subject: `Neue insur_tale Toolbox-Anfrage von ${formData.fullName}`,
          from_name: 'insur_tale Toolbox Join',
          // Form fields
          'Name': formData.fullName,
          'E-Mail-Adresse': formData.email,
          'Firma/Organisation': formData.company,
          'Rolle/Titel': formData.role,
          'LinkedIn Profil': formData.linkedinUrl || 'Nicht angegeben',
          'Grund für den Zugang': formData.useCase,
          // Metadata
          'Von': 'join.insurtale.io',
          'Submission Time': new Date().toLocaleString('de-DE', { timeZone: 'Europe/Berlin' }),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Senden fehlgeschlagen');
      }

      console.log('Senden erfolgreich:', result);

      setSubmissionState(SubmissionState.SUCCESS);
      onSuccess();
    } catch (error) {
      console.error('Senden fehlgeschlagen:', error);
      setSubmissionState(SubmissionState.ERROR);
    }
  };

  const isSubmitting = submissionState === SubmissionState.SUBMITTING;

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formHeader}>
        <h1>Zugang zur Toolbox erhalten</h1>
        <p className={styles.subtitle}>
          Fülle das Formular unten aus, dann erstelle ich dir einen Zugang zur Toolbox. - Jan
        </p>
      </div>

      {/* Full Name */}
      <div className={styles.formGroup}>
        <label htmlFor="fullName">
          Dein Name <span className={styles.required}>*</span>
        </label>
        <input
          type="text"
          id="fullName"
          name="fullName"
          value={formData.fullName}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          className={errors.fullName ? styles.inputError : ''}
        />
        {errors.fullName && <div className={styles.error}>{errors.fullName}</div>}
      </div>

      {/* Email */}
      <div className={styles.formGroup}>
        <label htmlFor="email">
          Deine E-Mail-Adresse <span className={styles.required}>*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          className={errors.email ? styles.inputError : ''}
        />
        {errors.email && <div className={styles.error}>{errors.email}</div>}
      </div>

      {/* Company */}
      <div className={styles.formGroup}>
        <label htmlFor="company">
          Deine Firma/Organisation <span className={styles.required}>*</span>
        </label>
        <input
          type="text"
          id="company"
          name="company"
          value={formData.company}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          className={errors.company ? styles.inputError : ''}
        />
        {errors.company && <div className={styles.error}>{errors.company}</div>}
      </div>

      {/* Role */}
      <div className={styles.formGroup}>
        <label htmlFor="role">
          Deine Rolle/Titel <span className={styles.required}>*</span>
        </label>
        <input
          type="text"
          id="role"
          name="role"
          value={formData.role}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          className={errors.role ? styles.inputError : ''}
        />
        {errors.role && <div className={styles.error}>{errors.role}</div>}
      </div>

      {/* LinkedIn URL */}
      <div className={styles.formGroup}>
        <label htmlFor="linkedinUrl">Dein LinkedIn Profil (Optional)</label>
        <input
          type="url"
          id="linkedinUrl"
          name="linkedinUrl"
          placeholder="https://linkedin.com/in/dein-profil"
          value={formData.linkedinUrl}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          className={errors.linkedinUrl ? styles.inputError : ''}
        />
        {errors.linkedinUrl && <div className={styles.error}>{errors.linkedinUrl}</div>}
      </div>

      {/* Use Case */}
      <div className={styles.formGroup}>
        <label htmlFor="useCase">
          Was interessiert dich an der insur_tale Toolbox besonders? <span className={styles.required}>*</span>
        </label>
        <textarea
          id="useCase"
          name="useCase"
          rows={5}
          value={formData.useCase}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={isSubmitting}
          className={errors.useCase ? styles.inputError : ''}
          placeholder="Beschreibe, was dich an der insur_tale Toolbox besonders interessiert und wie du denkst, dass sie dir helfen kann..."
        />
        <div className={styles.charCount}>
          {formData.useCase.length} / 1000 Zeichen
        </div>
        {errors.useCase && <div className={styles.error}>{errors.useCase}</div>}
      </div>

      {/* Honeypot field (hidden from users, for bot detection) */}
      <input
        type="text"
        name="honeypot"
        value={formData.honeypot}
        onChange={handleInputChange}
        tabIndex={-1}
        autoComplete="off"
        className={styles.honeypot}
        aria-hidden="true"
      />

      {/* Error message */}
      {submissionState === SubmissionState.ERROR && (
        <div className={styles.errorBanner}>
          Etwas ist schiefgelaufen. Bitte versuche es später erneut.
        </div>
      )}

      {/* Submit button */}
      <div className={styles.actions}>
        <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
          {isSubmitting ? 'Senden...' : 'Absenden'}
        </button>
      </div>‚
    </form>
  );
};

export default ApplicationForm;
